import { Box, Button, Flex, Icon } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { FaEdit } from "react-icons/fa";
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGetOrganizationEntryQuery, useGetOrganizationQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../common/subscription/useSub";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { OrganizationEntryModal } from "../entry/view/OrganizationEntryModal";

import { t } from "i18next";
import { urls } from "../../../urls";
import { useOrganizationProfileUrls } from "../useOrganizationProfileUrls";
import { LinkWithExistingOrgModal } from "../entry/view/LinkWithExistingOrgModal";
import { Banner } from "./Banner";
import { OrgProfileTabs } from "./OrgProfileTabs";
import { OrgProfileStatusTag } from "./OrgProfileStatusTag";
import { OrganizationProfileSkeleton } from "./OrganizationProfileSkeleton";
import { ContractsSection } from "./sections/ContractsSection";
import { InformationSection } from "./sections/InformationSection";
import { ReviewsRiskSection } from "./sections/ReviewsRiskSection";
import { SourcingEventsSection } from "./sections/SourcingEventsSection";

export const ViewOrganizationProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profileUrls = useOrganizationProfileUrls();
  const authState = useLoggedInWithOrgContextState();
  const { hasOrganizationAssessmentAccess } = useSub();
  const [showEntryModal, setShowEntryModal] = useState<string>();
  const [searchParams] = useSearchParams();

  const pathParts = location.pathname.split("/");
  const currentSection = pathParts[pathParts.length - 1] || "information";

  const orgId = searchParams.get("orgId");
  const entryId = searchParams.get("entryId");

  const {
    data: orgData,
    isLoading: orgLoading,
    error: orgError,
  } = useGetOrganizationQuery(orgId ? { orgId } : skipToken);

  const {
    data: entryData,
    isLoading: entryLoading,
    error: entryError,
  } = useGetOrganizationEntryQuery(entryId ? { entryId } : skipToken);

  const [organizationEntryToLink, setOrganizationEntryToLink] = useState<string | null>(null);

  if (entryData?.link?.organizationId && entryId && !orgId) {
    const orgLinkUrl = profileUrls.go(entryData.link.organizationId, currentSection);
    return <Navigate to={orgLinkUrl} replace />;
  }

  if ((orgId && orgLoading) || (entryId && entryLoading)) {
    return <OrganizationProfileSkeleton />;
  }

  if ((orgError && entryError) || (!orgLoading && !entryLoading && !orgData && !entryData)) {
    return (
      <Flex justify="center" alignItems="center" height="50vh">
        <Box textAlign="center" p={8}>
          <Box fontSize="xl" fontWeight="medium" color="gray.500">
            {t("Organization not found")}
          </Box>
        </Box>
      </Flex>
    );
  }

  const canEdit = !!orgData && authState.selectedOrg.canEdit && orgData?.id === authState.selectedOrg.id;

  const modals = (
    <>
      {organizationEntryToLink && (
        <LinkWithExistingOrgModal
          entryId={organizationEntryToLink}
          onClose={() => {
            setOrganizationEntryToLink(null);
          }}
        />
      )}
      {showEntryModal && (
        <OrganizationEntryModal
          entryId={showEntryModal}
          onClose={() => setShowEntryModal(undefined)}
          linkWithExistingOrg={() => {
            setShowEntryModal(undefined);
            setOrganizationEntryToLink(entryId);
          }}
        />
      )}
    </>
  );

  const banner = (orgData || entryData) && (
    <Banner
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name={orgData ? orgData.name : entryData!.name}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      country={orgData ? orgData.country : entryData!.country}
      tag={<OrgProfileStatusTag isRegistered={!!orgData} />}
    />
  );

  const actionButtons = (
    <Flex gap="2" flexWrap="wrap">
      {/* Edit button for organizations */}
      {canEdit && (
        <Explanation enabled={!canEdit} text={t("Only admins can edit the organization")}>
          <Button
            leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
            variant="outline"
            colorScheme="teal"
            size="sm"
            isDisabled={!canEdit}
            onClick={() => navigate(urls.app.organizationProfileEdit)}
          >
            {t("Edit")}
          </Button>
        </Explanation>
      )}
      {!orgData && entryData && authState.selectedOrg.canEdit && (
        <Button
          leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
          variant="outline"
          colorScheme="teal"
          size="sm"
          onClick={() => setShowEntryModal(entryData.id)}
        >
          {t("Edit")}
        </Button>
      )}
      {!authState.selectedOrg.canEdit && authState.selectedOrg.canLinkOrganizationEntry && entryData && (
        <Button
          leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
          variant="outline"
          colorScheme="teal"
          size="sm"
          whiteSpace="nowrap"
          onClick={() => setOrganizationEntryToLink(entryData.id)}
        >
          {t("Link to another organization")}
        </Button>
      )}
    </Flex>
  );

  const sectionRoutes = (
    <Routes>
      <Route path="information" element={<InformationSection organization={orgData} organizationEntry={entryData} />} />
      <Route path="contracts" element={<ContractsSection organization={orgData} organizationEntry={entryData} />} />
      <Route
        path="sourcing-events"
        element={<SourcingEventsSection organization={orgData} organizationEntry={entryData} />}
      />
      {hasOrganizationAssessmentAccess && (
        <Route
          path="reviews-risk"
          element={<ReviewsRiskSection organization={orgData} organizationEntry={entryData} />}
        />
      )}
      <Route
        path="*"
        element={(() => {
          const queryString = searchParams.toString();
          return <Navigate to={`information${queryString ? `?${queryString}` : ""}`} replace />;
        })()}
      />
    </Routes>
  );

  return (
    <>
      {modals}
      <Box maxW="container.xl" mx="auto">
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
          <Flex justifyContent="space-between" gap="3" flexWrap="wrap">
            {banner}
            {actionButtons}
          </Flex>
        </Box>
        <Box mt="6">
          <OrgProfileTabs />
        </Box>
        <Box pt="6">{sectionRoutes}</Box>
      </Box>
    </>
  );
};
